import { useState, useEffect, useRef } from 'react'
import { useFetchData } from '../../hooks/useFetchData'
import { Clock, CheckCircle, Loader, AlertCircle } from 'lucide-react'

export default function WATPractice({ state, updateState }) {
  const { data, loading, error } = useFetchData('/data/wat_words.csv')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [sentence, setSentence] = useState('')
  const [timeLeft, setTimeLeft] = useState(15)
  const [isActive, setIsActive] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [attempts, setAttempts] = useState([])
  const inputRef = useRef(null)

  const words = data || []
  const currentWord = words[currentIndex]?.word

  useEffect(() => {
    let interval = null
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => time - 1)
      }, 1000)
    } else if (timeLeft === 0 && isActive) {
      handleSubmit()
    }
    return () => clearInterval(interval)
  }, [isActive, timeLeft])

  const startWord = () => {
    setIsActive(true)
    setTimeLeft(15)
    setSentence('')
    setSubmitted(false)
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const handleSubmit = () => {
    setIsActive(false)
    setSubmitted(true)

    const attempt = {
      word: currentWord,
      sentence,
      timestamp: new Date().toISOString(),
      timeUsed: 15 - timeLeft,
    }

    const newAttempts = [...attempts, attempt]
    setAttempts(newAttempts)

    // Save to state
    updateState('progress.wat.attempts', [
      ...(state.progress.wat?.attempts || []),
      attempt,
    ])
    updateState('progress.wat.totalWords', (state.progress.wat?.totalWords || 0) + 1)
  }

  const handleNext = () => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1)
      setSubmitted(false)
      setSentence('')
    } else {
      setCurrentIndex(0)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading words...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-800 dark:text-red-200">Error Loading Words</h3>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">WAT Practice</h2>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Completed: {attempts.length}
        </div>
      </div>

      <div className="card bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500">
        <h3 className="font-bold mb-2">Instructions</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          You have 15 seconds to form a meaningful sentence using the given word.
          The sentence should reflect positive qualities and officer-like qualities.
        </p>
      </div>

      <div className="card">
        <div className="text-center mb-6">
          {isActive && (
            <div className="mb-4 flex items-center justify-center">
              <Clock className={`w-6 h-6 mr-2 ${timeLeft <= 5 ? 'text-red-600 animate-pulse' : 'text-gray-600'}`} />
              <span className={`text-3xl font-bold ${timeLeft <= 5 ? 'text-red-600' : 'text-gray-900 dark:text-gray-100'}`}>
                {timeLeft}s
              </span>
            </div>
          )}

          <div className="mb-6">
            <span className="text-sm text-gray-500 dark:text-gray-400">Word {currentIndex + 1} of {words.length}</span>
            <h3 className="text-4xl font-bold text-primary-600 dark:text-primary-400 mt-2 uppercase">
              {currentWord}
            </h3>
            {words[currentIndex]?.theme && (
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 inline-block">
                Theme: {words[currentIndex].theme} | Level: {words[currentIndex].level}
              </span>
            )}
          </div>

          {!isActive && !submitted && (
            <button onClick={startWord} className="btn-primary">
              Start Timer
            </button>
          )}

          {isActive && (
            <div className="space-y-4">
              <textarea
                ref={inputRef}
                value={sentence}
                onChange={(e) => setSentence(e.target.value)}
                placeholder="Type your sentence here..."
                className="textarea-field"
                rows="3"
                autoFocus
              />
              <button onClick={handleSubmit} className="btn-primary w-full">
                Submit
              </button>
            </div>
          )}

          {submitted && (
            <div className="space-y-4 animate-slide-in">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-semibold text-green-800 dark:text-green-200">Submitted!</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <strong>Your sentence:</strong> {sentence || '(No sentence written)'}
                </p>
              </div>

              <button onClick={handleNext} className="btn-primary w-full">
                Next Word
              </button>
            </div>
          )}
        </div>
      </div>

      {attempts.length > 0 && (
        <div className="card">
          <h3 className="font-bold mb-4">Recent Attempts</h3>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {attempts.slice(-5).reverse().map((attempt, index) => (
              <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-semibold text-primary-600 dark:text-primary-400 uppercase">
                    {attempt.word}
                  </span>
                  <span className="text-xs text-gray-500">
                    {attempt.timeUsed}s
                  </span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{attempt.sentence}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
